import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Spinner, Button, Collapse, Modal } from 'react-bootstrap';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { listarRango, cambiarEstado } from '../../services/turneroService';
import { colorTramite, bgTramite, formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { derivarAtencion } from '../../constants/areasAtencion';
import { estiloEstado } from '../../constants/estadosTurno';
import { esSinPresentacion, metaTipoTurnoAnses } from '../../utils/tiposTurnoAnses';
import SelectorAreaAtencion from '../../components/recepcion/SelectorAreaAtencion';
import ModalReingresoCliente, { TIPO_REATENCION, TIPO_SOLO_DOCUMENTACION, TIPO_APERTURA_PLAN } from '../../components/recepcion/ModalReingresoCliente';
import BadgeReingreso from '../../components/recepcion/BadgeReingreso';
import ModalLlegoCliente, { LLEGADA_EXPRES, LLEGADA_REATENCION, LLEGADA_SIN_TURNO } from '../../components/recepcion/ModalLlegoCliente';
import ModalBuscarAtendidoHoy from '../../components/recepcion/ModalBuscarAtendidoHoy';
import ModalTramiteExpres from '../../components/recepcion/ModalTramiteExpres';
import ChipMotivoExpres from '../../components/recepcion/ChipMotivoExpres';
import { ultimaAtencionFinalizada } from '../../utils/ultimaAtencionCliente';
import CustomModal from '../../components/CustomModal';
import useCustomModal from '../../hooks/useCustomModal';
import { ModalCrearTurno, ModalDetalleTurno } from './TurneroPage';
import PageHeader from '../../components/ui/PageHeader';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
import turneroStyles from '../../style/pages/Turnero.module.css';
import '../../style/pages/HomePage.css';
import '../../style/pages/PanelDiario.css';

import { apellidoNombre } from '../../utils/nombrePersona';
import { coincideBusqueda } from '../../utils/textoComparable';
const INTERVALO_ACTUALIZACION_MS = 20000;

const ESTADOS_CHECKIN = new Set(['CREADO', 'PENDIENTE', 'CONFIRMADO', 'REPROGRAMADO']);

// Subconjunto de estados relevante para el filtro del día (incluye "Todos" y agrupa
// ASISTIO bajo la etiqueta que ya usa este panel, "Asistió / En espera").
const FILTROS_ESTADO_PANEL = [
    { valor: '', etiqueta: 'Todos los estados' },
    { valor: 'CREADO', etiqueta: 'Creado' },
    { valor: 'CONFIRMADO', etiqueta: 'Confirmado' },
    { valor: 'REPROGRAMADO', etiqueta: 'Reprogramado' },
    { valor: 'ASISTIO', etiqueta: 'Asistió / En espera' },
    { valor: 'EN_CURSO', etiqueta: 'En curso' },
    { valor: 'EN_ANSES', etiqueta: 'En ANSES' },
    // Los que fueron a ANSES por un plan de pago y vuelven a abrirlo, y los que ya volvieron.
    { valor: 'PLAN', etiqueta: 'Plan de pago: vuelven de ANSES' },
    { valor: 'NO_ASISTIO', etiqueta: 'No asistió' },
    { valor: 'CANCELADO', etiqueta: 'Cancelado' },
];

function fechaISOHoy() {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
}

function normalizarHorario(horario) {
    return horario ? String(horario).substring(0, 5) : '00:00';
}

function formatearHorario(horario) {
    return horario ? normalizarHorario(horario) : '-';
}

function campoValor(valor) {
    return valor ? String(valor) : '-';
}

function resolverIdTurno(turno) {
    return turno?.idTurno ?? turno?.id ?? turno?.id_turno;
}

const PanelDiario = () => {
    const navigate = useNavigate();
    const montadoRef = useRef(false);

    const [turnos, setTurnos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [procesandoId, setProcesandoId] = useState(null);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
    const [mostrarFinalizados, setMostrarFinalizados] = useState(false);
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const { modalState, hideModal } = useCustomModal();

    const cargarTurnos = useCallback(async (silencioso = false) => {
        if (!silencioso) setCargando(true);
        if (!silencioso) setError('');
        try {
            const hoyISO = fechaISOHoy();
            const datos = await listarRango(hoyISO, hoyISO);
            if (!montadoRef.current) return;
            const lista = (Array.isArray(datos) ? datos : []).slice().sort((a, b) => {
                return normalizarHorario(a?.horario).localeCompare(normalizarHorario(b?.horario));
            });
            setTurnos(lista);
            setUltimaActualizacion(new Date());
        } catch (e) {
            if (!montadoRef.current) return;
            setError(e?.response?.data?.mensaje || 'No se pudieron cargar los turnos de hoy.');
        } finally {
            if (montadoRef.current) setCargando(false);
        }
    }, []);

    useEffect(() => {
        montadoRef.current = true;
        cargarTurnos(false);
        const intervalo = setInterval(() => cargarTurnos(true), INTERVALO_ACTUALIZACION_MS);
        return () => {
            montadoRef.current = false;
            clearInterval(intervalo);
        };
    }, [cargarTurnos]);

    // Check-in ("Asistió"): antes de pasar a ASISTIO, recepción confirma o corrige el área
    // de atención a la que deriva al cliente.
    const [turnoCheckin, setTurnoCheckin] = useState(null);
    const [areaCheckin, setAreaCheckin] = useState('');
    const [errorCheckin, setErrorCheckin] = useState('');
    const [mostrarEspontaneo, setMostrarEspontaneo] = useState(false);

    const abrirCheckin = useCallback((turno) => {
        setErrorCheckin('');
        setAreaCheckin(turno?.atencion || derivarAtencion(turno?.tipoTramite, turno?.modalidad));
        setTurnoCheckin(turno);
    }, []);

    const cerrarCheckin = useCallback(() => {
        if (procesandoId) return;
        setTurnoCheckin(null);
        setAreaCheckin('');
        setErrorCheckin('');
    }, [procesandoId]);

    const confirmarCheckin = useCallback(async () => {
        const idTurno = resolverIdTurno(turnoCheckin);
        if (!idTurno || !areaCheckin || procesandoId) return;
        setProcesandoId(idTurno);
        try {
            await cambiarEstado(idTurno, 'ASISTIO', { atencion: areaCheckin });
            await cargarTurnos(true);
            if (montadoRef.current) {
                setTurnoCheckin(null);
                setAreaCheckin('');
            }
        } catch (e) {
            if (montadoRef.current) {
                setErrorCheckin(e?.response?.data?.mensaje || 'No se pudo registrar la asistencia del turno.');
            }
        } finally {
            if (montadoRef.current) setProcesandoId(null);
        }
    }, [turnoCheckin, areaCheckin, procesandoId, cargarTurnos]);

    // Ver detalles: abre el mismo modal de detalle/edición que TurneroPage usa al hacer clic
    // en una tarjeta de la agenda (ModalDetalleTurno, exportado desde TurneroPage.js).
    const [turnoDetalle, setTurnoDetalle] = useState(null);

    const abrirDetalle = useCallback((turno) => {
        setTurnoDetalle(turno);
    }, []);

    const cerrarDetalle = useCallback(() => {
        setTurnoDetalle(null);
    }, []);

    const handleDetalleActualizado = useCallback((actualizado) => {
        setTurnoDetalle(actualizado);
        setTurnos((prev) => prev.map((t) => resolverIdTurno(t) === resolverIdTurno(actualizado) ? actualizado : t));
    }, []);

    const handleDetalleEliminado = useCallback(() => {
        cerrarDetalle();
        cargarTurnos(true);
    }, [cerrarDetalle, cargarTurnos]);

    const handleDetalleAsistio = useCallback(() => {
        cerrarDetalle();
        cargarTurnos(true);
    }, [cerrarDetalle, cargarTurnos]);

    // Ingreso espontáneo: se crea el turno (ya como sobreturno de hoy, vía ModalCrearTurno en
    // modo espontáneo) y de inmediato se lo pasa a ASISTIO con el área elegida en el mismo modal.
    const confirmarEspontaneo = useCallback(async (turnoCreado) => {
        setMostrarEspontaneo(false);
        const idTurno = resolverIdTurno(turnoCreado);
        if (!idTurno) {
            await cargarTurnos(true);
            return;
        }
        try {
            await cambiarEstado(idTurno, 'ASISTIO', { atencion: turnoCreado?.atencion });
        } catch (e) {
            if (montadoRef.current) {
                setError(e?.response?.data?.mensaje || 'El cliente se registró, pero no se pudo enviarlo a la cola de atención.');
            }
        } finally {
            await cargarTurnos(true);
        }
    }, [cargarTurnos]);

    // Reingreso ("Volvió cliente"): el cliente de un turno ya finalizado vuelve al estudio. El
    // backend crea un turno nuevo enganchado al finalizado; acá solo se refresca la lista.
    const [turnoReingreso, setTurnoReingreso] = useState(null);
    // Qué opción del modal de reingreso aparece elegida: "Llegó cliente" ya lo decidió antes.
    const [tipoReingresoInicial, setTipoReingresoInicial] = useState(TIPO_REATENCION);
    // true cuando el tipo ya se eligió en "Llegó cliente": el modal no lo vuelve a preguntar.
    const [tipoReingresoFijo, setTipoReingresoFijo] = useState(false);

    const cerrarReingreso = useCallback(() => setTurnoReingreso(null), []);

    const abrirReingreso = useCallback((turno, tipo = TIPO_REATENCION, fijo = false) => {
        setTipoReingresoInicial(tipo);
        setTipoReingresoFijo(fijo);
        setTurnoReingreso(turno);
    }, []);

    // Desde el detalle se cierra primero su overlay: el modal de reingreso es de Bootstrap y
    // quedaría tapado por el overlay del turnero.
    const abrirReingresoDesdeDetalle = useCallback((turno) => {
        setTurnoDetalle(null);
        abrirReingreso(turno);
    }, [abrirReingreso]);

    const handleReingresoRegistrado = useCallback(async () => {
        setTurnoReingreso(null);
        await cargarTurnos(true);
    }, [cargarTurnos]);

    // "Llegó cliente": primero se elige a qué viene (ModalLlegoCliente) y después se abre el
    // flujo que corresponde. Exprés y re-atención buscan entre los atendidos de hoy, igual que
    // "Volvió cliente" del Turnero; el exprés además admite a alguien sin turno previo.
    //   null | 'SELECTOR' | 'BUSCAR_REATENCION' | 'BUSCAR_EXPRES' | 'EXPRES_SIN_TURNO'
    const [flujoLlegada, setFlujoLlegada] = useState(null);

    const cerrarFlujoLlegada = useCallback(() => setFlujoLlegada(null), []);

    const elegirLlegada = useCallback((opcion) => {
        if (opcion === LLEGADA_SIN_TURNO) {
            setFlujoLlegada(null);
            setMostrarEspontaneo(true);
        } else if (opcion === LLEGADA_EXPRES) {
            setFlujoLlegada('BUSCAR_EXPRES');
        } else if (opcion === LLEGADA_REATENCION) {
            setFlujoLlegada('BUSCAR_REATENCION');
        }
    }, []);

    // El buscador vive en el overlay del turnero y el reingreso es Bootstrap: se cierra uno antes
    // de abrir el otro para que no quede tapado.
    const elegirAtendidoHoy = useCallback((turno) => {
        const tipo = flujoLlegada === 'BUSCAR_EXPRES' ? TIPO_SOLO_DOCUMENTACION : TIPO_REATENCION;
        setFlujoLlegada(null);
        abrirReingreso(turno, tipo, true);
    }, [flujoLlegada, abrirReingreso]);

    // Cliente del padrón que no fue atendido hoy. Exprés: se registra sin turno previo, con sus
    // datos ya cargados. Re-atención: vuelve por su última atención finalizada, de cualquier día;
    // si no tiene ninguna, el buscador muestra el error y no se abre nada.
    const [clienteExpres, setClienteExpres] = useState(null);

    const elegirClientePadron = useCallback(async (cliente) => {
        if (flujoLlegada === 'BUSCAR_EXPRES') {
            setClienteExpres(cliente);
            setFlujoLlegada('EXPRES_SIN_TURNO');
            return;
        }
        const origen = await ultimaAtencionFinalizada(cliente);
        setFlujoLlegada(null);
        abrirReingreso(origen, TIPO_REATENCION, true);
    }, [flujoLlegada, abrirReingreso]);

    const abrirExpresSinCliente = useCallback(() => {
        setClienteExpres(null);
        setFlujoLlegada('EXPRES_SIN_TURNO');
    }, []);

    const handleExpresRegistrado = useCallback(async () => {
        setFlujoLlegada(null);
        setClienteExpres(null);
        await cargarTurnos(true);
    }, [cargarTurnos]);

    // Turnos finalizados que ya tienen un reingreso todavía abierto: no se ofrece otro "Volvió
    // cliente" sobre ellos (el backend también lo rechaza).
    const origenesConReingresoAbierto = useMemo(() => {
        const abiertos = new Set(['ASISTIO', 'EN_CURSO', 'EN_ANSES']);
        return new Set(turnos
            .filter((t) => t?.esReingreso && t?.idTurnoOrigen != null
                && abiertos.has(String(t?.estado || '').trim().toUpperCase()))
            .map((t) => t.idTurnoOrigen));
    }, [turnos]);

    // El que salió hacia ANSES (EN_ANSES: solo el plan de pago) ya no está en el estudio: no es
    // un agendado del día, va con los terminados y desde ahí se marca "Volvió de ANSES".
    const terminado = (t) => ['FINALIZADO', 'EN_ANSES'].includes(String(t?.estado || '').trim().toUpperCase());

    const agendados = useMemo(
        () => turnos.filter((t) => !terminado(t)),
        [turnos]
    );

    const agendadosFiltrados = useMemo(() => {
        const est = filtroEstado.trim().toUpperCase();
        return agendados.filter((t) => {
            // Sin tildes y por palabras: ver coincideBusqueda.
            const coincideTexto = coincideBusqueda(
                [t.apellido, t.nombre, t.cuil, t.dni, t.telefonoCliente], filtroTexto);
            const coincideEstado = !est
                || (est === 'PLAN' ? (t?.vuelvePorPlan || t?.tipoReingreso === TIPO_APERTURA_PLAN)
                    : String(t?.estado || '').trim().toUpperCase() === est);
            return coincideTexto && coincideEstado;
        });
    }, [agendados, filtroTexto, filtroEstado]);

    const finalizadosHoy = useMemo(
        () => turnos.filter(terminado),
        [turnos]
    );

    const cantidadExpresHoy = useMemo(
        () => finalizadosHoy.filter((t) => t?.esTramiteExpres).length,
        [finalizadosHoy]
    );

    const kpis = useMemo(() => {
        let totalAgendados = 0;
        let enEspera = 0;
        let enAtencion = 0;
        let finalizados = 0;
        let espontaneos = 0;
        turnos.forEach((t) => {
            const estado = String(t?.estado || '').trim().toUpperCase();
            // En espera y en atención muestran la sala tal como está, reingresos incluidos.
            if (estado === 'ASISTIO') enEspera += 1;
            else if (estado === 'EN_CURSO') enAtencion += 1;
            // Los reingresos y los trámites exprés no son visitas nuevas: no suman a los totales
            // del día, igual que en los reportes de Recepción.
            if (t?.esReingreso || t?.esTramiteExpres) return;
            totalAgendados += 1;
            if (estado === 'FINALIZADO' || estado === 'EN_ANSES') finalizados += 1;
            if (t?.esSobreturno) espontaneos += 1;
        });
        return { totalAgendados, enEspera, enAtencion, finalizados, espontaneos };
    }, [turnos]);

    const irAColaDeClientes = () => navigate('/recepcion/atencion');

    return (
        <div className="homepage-container area-recepcion">
            <PageHeader
                variante="plano"
                icono="speedometer2"
                titulo="Panel Diario"
                descripcion={`Actividad de recepción en tiempo real — hoy, ${new Date().toLocaleDateString('es-AR')}`}
            />

            {/* Acciones - FUERA del header, misma posición que el botón "Nuevo turno" en
                TurneroPage y "Configurar Pantalla Informativa" en AtencionPage. */}
            <div className="panel-diario-acciones-container">
                <Button className="rec-btn-accent" onClick={() => setFlujoLlegada('SELECTOR')}>
                    <i className="bi bi-door-open-fill me-2"></i>
                    Llegó cliente
                </Button>
                <Button className="rec-btn-primary" onClick={() => window.open('/herramientas/historial-laboral', '_blank', 'noopener')}>
                    <i className="bi bi-calculator me-2"></i>
                    Calculadora de aportes
                </Button>
                <Button className="rec-btn-secondary" onClick={irAColaDeClientes}>
                    <i className="bi bi-arrow-right-circle-fill me-2"></i>
                    Ir a cola de clientes
                </Button>
                <Button className="rec-btn-secondary" onClick={() => navigate('/recepcion/turnero')}>
                    <i className="bi bi-calendar3 me-2"></i>
                    Ir al Turnero
                </Button>
            </div>

            <div className="ds-kpis ds-kpis--5 mb-4">
                <TarjetaKpi label="Total Agendados" valor={kpis.totalAgendados} icono="calendar2-check" tono="recepcion" />
                <TarjetaKpi label="En Espera" valor={kpis.enEspera} icono="clock-history" tono="teal" />
                <TarjetaKpi label="En Atención" valor={kpis.enAtencion} icono="play-circle-fill" tono="advertencia" />
                <TarjetaKpi label="Finalizados" valor={kpis.finalizados} icono="check-circle-fill" tono="exito" />
                <TarjetaKpi label="Sin Turno / Espontáneos" valor={kpis.espontaneos} icono="exclamation-diamond-fill" tono="violeta" />
            </div>

            <div className="eventos-section rec-panel panel-diario-section">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-calendar3 me-2"></i>
                        Turnos Agendados del Día
                    </h2>
                    <div className="panel-diario-header-acciones">
                        {ultimaActualizacion && (
                            <span className="panel-diario-ultima-actualizacion">
                                Actualizado {ultimaActualizacion.toLocaleTimeString('es-AR')}
                            </span>
                        )}
                        <button
                            type="button"
                            className="rec-btn-icono"
                            onClick={() => cargarTurnos(false)}
                            disabled={cargando}
                            title="Actualizar ahora"
                        >
                            <i className={`bi bi-arrow-clockwise ${cargando ? 'panel-diario-girando' : ''}`}></i>
                        </button>
                        <Badge bg="primary" className="rec-total-badge">
                            {agendadosFiltrados.length}{agendadosFiltrados.length !== agendados.length ? ` / ${agendados.length}` : ''} agendados
                        </Badge>
                    </div>
                </div>

                <div className={turneroStyles.filtrosFila} style={{ marginBottom: '1.25rem' }}>
                    <div className={turneroStyles.filtroCampoAncho}>
                        <label className={turneroStyles.filtroLabel}>
                            <i className="bi bi-search me-2"></i>Buscar
                        </label>
                        <input
                            type="text"
                            className={`form-control ${turneroStyles.filtroInput}`}
                            placeholder="Nombre, CUIL, DNI o teléfono…"
                            value={filtroTexto}
                            onChange={(e) => setFiltroTexto(e.target.value)}
                        />
                    </div>
                    <div className={turneroStyles.filtroCampo}>
                        <label className={turneroStyles.filtroLabel}>
                            <i className="bi bi-flag me-2"></i>Estado
                        </label>
                        <select
                            className={`form-select ${turneroStyles.filtroInput}`}
                            value={filtroEstado}
                            onChange={(e) => setFiltroEstado(e.target.value)}
                        >
                            {FILTROS_ESTADO_PANEL.map((f) => (
                                <option key={f.valor || 'TODOS'} value={f.valor}>{f.etiqueta}</option>
                            ))}
                        </select>
                    </div>
                    {(filtroTexto || filtroEstado) && (
                        <button type="button" className={`rec-btn-secondary ${turneroStyles.filtroBotonLimpiar}`} onClick={() => { setFiltroTexto(''); setFiltroEstado(''); }}>
                            Limpiar
                        </button>
                    )}
                </div>

                {cargando ? (
                    <div className="loading-state">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 mb-0">Cargando turnos del día...</p>
                    </div>
                ) : error ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-exclamation-triangle"></i>
                        <h5>Ocurrió un problema</h5>
                        <p>{error}</p>
                    </div>
                ) : agendados.length === 0 ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-calendar-x"></i>
                        <h5>No hay turnos agendados para hoy</h5>
                        <p>Todavía no se registraron turnos para la fecha actual.</p>
                    </div>
                ) : agendadosFiltrados.length === 0 ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-funnel"></i>
                        <h5>Ningún turno coincide con el filtro</h5>
                        <p>Probá con otro texto o estado.</p>
                    </div>
                ) : (
                    <div className="panel-diario-lista">
                        {agendadosFiltrados.map((turno) => {
                            const idTurno = resolverIdTurno(turno);
                            const estadoInfo = estiloEstado(turno?.estado);
                            const claveEstado = String(turno?.estado || '').trim().toUpperCase();
                            // ASISTIO en este panel significa "el cliente está esperando"; se muestra
                            // con esa etiqueta en vez de "Asistió" (que sí corresponde en el Turnero).
                            const etiquetaEstado = claveEstado === 'ASISTIO' && estadoInfo.etiquetaEspera
                                ? estadoInfo.etiquetaEspera
                                : estadoInfo.etiqueta;
                            const tipoColor = colorTramite(turno.tipoTramite);
                            const tipoBg = bgTramite(turno.tipoTramite);
                            const puedeCheckin = ESTADOS_CHECKIN.has(claveEstado);
                            return (
                                <div key={idTurno ?? `${turno.apellido}-${turno.nombre}-${turno.horario}`} className="rec-item panel-diario-item">
                                    <span
                                        className={`rec-item-riel ${turno.esSobreturno ? 'rec-item-riel--sobreturno' : ''}`}
                                        style={turno.esSobreturno ? undefined : { background: estadoInfo.riel }}
                                    />
                                    <div className="panel-diario-item-hora">
                                        <AccessTimeIcon fontSize="small" className="me-1" />
                                        {formatearHorario(turno.horario)}
                                    </div>
                                    <div className="panel-diario-item-cliente">
                                        <div className="panel-diario-item-nombre">
                                            {apellidoNombre(turno.apellido, turno.nombre) || '-'}
                                        </div>
                                        <div className="panel-diario-item-sub">
                                            <BadgeIcon fontSize="small" className="me-1" />
                                            {campoValor(turno.dni || turno.cuil || turno.numeroIdentificacion)}
                                            {turno.referencia && (
                                                <span className="panel-diario-item-sub-referencia">
                                                    <i className="bi bi-chat-left-text me-1"></i>
                                                    {turno.referencia}
                                                </span>
                                            )}
                                        </div>
                                        {turno.motivo && (
                                            <div className="panel-diario-item-motivo" title={turno.motivo}>
                                                <i className="bi bi-chat-left-quote-fill"></i>
                                                <span>{turno.motivo}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="panel-diario-item-tramite">
                                        <span
                                            className="rec-chip-tramite"
                                            style={{ backgroundColor: tipoBg, color: tipoColor, borderColor: `${tipoColor}33` }}
                                        >
                                            <ReceiptLongIcon className="panel-diario-tramite-badge-icono" style={{ color: tipoColor }} />
                                            {formatearTipoTramite(turno.tipoTramite)}
                                        </span>
                                        {/* El área de atención recién se define al marcar "Asistió" (abrirCheckin),
                                            así que mostrarla acá anticiparía una decisión que todavía no se tomó. */}
                                        {turno.esSobreturno && !turno.esReingreso && (
                                            <span className="rec-badge-sobreturno">
                                                <i className="bi bi-exclamation-diamond me-1"></i>
                                                Sobreturno
                                            </span>
                                        )}
                                        {/* El reingreso nace como sobreturno (no ocupa bloque de la grilla),
                                            pero lo que le importa a recepción es que ya estuvo hoy. */}
                                        <BadgeReingreso turno={turno} />
                                        {turno.vuelvePorPlan && (
                                            <span className="rec-chip-tramite" title="Salió hacia ANSES por el plan de pago y vuelve a abrirlo"
                                                style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#f59e0b33' }}>
                                                <i className="bi bi-hourglass-split me-1"></i>Vuelve por el plan
                                            </span>
                                        )}
                                        {turno.tipoReingreso === TIPO_APERTURA_PLAN && (
                                            <span className="rec-chip-tramite" title="Volvió de ANSES: hay que abrir el plan de pago"
                                                style={{ backgroundColor: '#dcfce7', color: '#166534', borderColor: '#16a34a33' }}>
                                                <i className="bi bi-cash-coin me-1"></i>Apertura del plan
                                            </span>
                                        )}
                                        {/* No se atiende en el mostrador: espera y sale con un abogado. */}
                                        {turno.modalidad === 'ANSES' && (
                                            <span
                                                className="rec-chip-tramite"
                                                style={{ backgroundColor: '#e0f2fe', color: '#0369a1', borderColor: '#0ea5e933' }}
                                                title={esSinPresentacion(turno.tipoTurnoAnses)
                                                    ? metaTipoTurnoAnses(turno.tipoTurnoAnses).detalleTurnero
                                                    : (turno.horaTurnoAnses ? `Turno en ANSES ${turno.horaTurnoAnses}` : 'Turno ANSES')}
                                            >
                                                <i className={`bi ${esSinPresentacion(turno.tipoTurnoAnses) ? metaTipoTurnoAnses(turno.tipoTurnoAnses).icono : 'bi-building'} me-1`}></i>
                                                {esSinPresentacion(turno.tipoTurnoAnses)
                                                    ? metaTipoTurnoAnses(turno.tipoTurnoAnses).enTurnero
                                                    : `Turno ANSES${turno.horaTurnoAnses ? ` · ${turno.horaTurnoAnses}` : ''}`}
                                            </span>
                                        )}
                                        <span
                                            className="rec-badge-estado"
                                            style={{ background: estadoInfo.bg, border: `1px solid ${estadoInfo.border}`, color: estadoInfo.text }}
                                        >
                                            <i className={`bi ${estadoInfo.icono} me-1`}></i>
                                            {etiquetaEstado}
                                        </span>
                                    </div>
                                    <div className="panel-diario-item-acciones-col">
                                        <div className="panel-diario-item-botones">
                                            {/* Fue a ANSES por el plan de pago y volvió: a la cola para abrirlo. */}
                                            {turno.vuelvePorPlan && !origenesConReingresoAbierto.has(idTurno) && (
                                                <button
                                                    type="button"
                                                    className="rec-btn-primary panel-diario-btn-checkin"
                                                    title="Volvió de ANSES para abrir el plan de pago"
                                                    onClick={() => abrirReingreso(turno, TIPO_APERTURA_PLAN, true)}
                                                >
                                                    <i className="bi bi-arrow-return-left me-1"></i>
                                                    Volvió de ANSES
                                                </button>
                                            )}
                                            {puedeCheckin && (
                                                <button
                                                    type="button"
                                                    className="rec-btn-primary panel-diario-btn-checkin"
                                                    disabled={procesandoId === idTurno}
                                                    onClick={() => abrirCheckin(turno)}
                                                >
                                                    {procesandoId === idTurno ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-person-check-fill me-1"></i>
                                                            Asistió
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="panel-diario-btn-detalle"
                                                title="Ver detalles"
                                                onClick={() => abrirDetalle(turno)}
                                            >
                                                <i className="bi bi-three-dots-vertical"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="eventos-section rec-panel panel-diario-section">
                <div
                    className="panel-diario-finalizados-header"
                    onClick={() => setMostrarFinalizados((prev) => !prev)}
                    role="button"
                    tabIndex={0}
                >
                    <div className="panel-diario-finalizados-titulo">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Turnos Finalizados del Día
                        <Badge bg="success" className="ms-3">{finalizadosHoy.length - cantidadExpresHoy} atendidos</Badge>
                        {cantidadExpresHoy > 0 && (
                            <span className="rec-badge-expres ms-2">
                                <i className="bi bi-lightning-charge-fill me-1"></i>
                                {cantidadExpresHoy} exprés
                            </span>
                        )}
                    </div>
                    <i className={`bi bi-chevron-${mostrarFinalizados ? 'up' : 'down'}`}></i>
                </div>

                <Collapse in={mostrarFinalizados}>
                    <div>
                        {finalizadosHoy.length === 0 ? (
                            <div className="empty-state-eventos compact">
                                <i className="bi bi-inboxes"></i>
                                <p>Todavía no se finalizó ninguna atención hoy.</p>
                            </div>
                        ) : (
                            <div className="panel-diario-lista-finalizados">
                                {finalizadosHoy.map((turno) => {
                                    const idTurno = resolverIdTurno(turno);
                                    const tipoColor = colorTramite(turno.tipoTramite);
                                    const tipoBg = bgTramite(turno.tipoTramite);
                                    const esExpres = !!turno.esTramiteExpres;
                                    return (
                                        <div
                                            key={idTurno ?? `${turno.apellido}-${turno.nombre}-fin`}
                                            className={`panel-diario-item-finalizado ${esExpres ? 'panel-diario-item-finalizado--expres' : ''}`}
                                            role="button"
                                            tabIndex={0}
                                            title="Ver detalles"
                                            onClick={() => abrirDetalle(turno)}
                                            onKeyDown={(e) => e.key === 'Enter' && abrirDetalle(turno)}
                                        >
                                            <div className="panel-diario-item-hora">
                                                <AccessTimeIcon fontSize="small" className="me-1" />
                                                {formatearHorario(turno.horario)}
                                            </div>
                                            <div className="panel-diario-item-cliente">
                                                <div className="panel-diario-item-nombre">
                                                    {apellidoNombre(turno.apellido, turno.nombre) || '-'}
                                                </div>
                                                {/* En el exprés lo que importa es qué dejó o retiró. */}
                                                {esExpres && turno.expresDocumentacion && (
                                                    <div className="panel-diario-item-motivo" title={turno.expresDocumentacion}>
                                                        <i className="bi bi-folder-symlink"></i>
                                                        <span>{turno.expresDocumentacion}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="panel-diario-item-tramite">
                                                {esExpres ? (
                                                    <ChipMotivoExpres turno={turno} conIcono />
                                                ) : (
                                                    <span
                                                        className="rec-chip-tramite"
                                                        style={{ backgroundColor: tipoBg, color: tipoColor, borderColor: `${tipoColor}33` }}
                                                    >
                                                        <ReceiptLongIcon className="panel-diario-tramite-badge-icono" style={{ color: tipoColor }} />
                                                        {formatearTipoTramite(turno.tipoTramite)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="panel-diario-item-operador">
                                                <i className="bi bi-person-badge me-1"></i>
                                                {campoValor(turno.operadorNombre || turno.usuarioAtiendeNombre)}
                                            </div>
                                            <div className="panel-diario-item-reingreso">
                                                <BadgeReingreso turno={turno} />
                                                {/* Salió hacia ANSES por el plan: su retorno es abrir el plan. */}
                                                {turno.vuelvePorPlan && !origenesConReingresoAbierto.has(idTurno) && (
                                                    <button
                                                        type="button"
                                                        className="rec-btn-primary panel-diario-btn-reingreso"
                                                        title="Volvió de ANSES para abrir el plan de pago"
                                                        onClick={(e) => { e.stopPropagation(); abrirReingreso(turno, TIPO_APERTURA_PLAN, true); }}
                                                    >
                                                        <i className="bi bi-arrow-return-left me-1"></i>
                                                        Volvió de ANSES
                                                    </button>
                                                )}
                                                {/* Del exprés no se "vuelve": no fue una atención. */}
                                                {!esExpres && !turno.vuelvePorPlan && !origenesConReingresoAbierto.has(idTurno) && (
                                                    <button
                                                        type="button"
                                                        className="rec-btn-secondary panel-diario-btn-reingreso"
                                                        title="El cliente volvió al estudio"
                                                        onClick={(e) => { e.stopPropagation(); abrirReingreso(turno); }}
                                                    >
                                                        <i className="bi bi-arrow-repeat me-1"></i>
                                                        Volvió cliente
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Collapse>
            </div>

            <Modal className="rec-modal" show={!!turnoCheckin} onHide={cerrarCheckin} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-person-check-fill me-2"></i>
                        Registrar asistencia
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {turnoCheckin && (
                        <div className="rec-modal-resumen panel-diario-checkin-resumen">
                            <div className="panel-diario-checkin-nombre">
                                {apellidoNombre(turnoCheckin.apellido, turnoCheckin.nombre) || '-'}
                            </div>
                            <div className="panel-diario-checkin-detalle">
                                <AccessTimeIcon fontSize="small" className="me-1" />
                                {formatearHorario(turnoCheckin.horario)}
                                <span className="mx-2">·</span>
                                <ReceiptLongIcon fontSize="small" className="me-1" />
                                {formatearTipoTramite(turnoCheckin.tipoTramite)}
                            </div>
                        </div>
                    )}
                    <label className="form-label fw-semibold mt-3 mb-2">¿A qué área lo derivamos? *</label>
                    <SelectorAreaAtencion valor={areaCheckin} onChange={setAreaCheckin} disabled={!!procesandoId} />
                    {errorCheckin && <div className="alert alert-danger py-2 mt-3 mb-0">{errorCheckin}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <button className="rec-btn-secondary" onClick={cerrarCheckin} disabled={!!procesandoId}>Cancelar</button>
                    <button className="rec-btn-primary" onClick={confirmarCheckin} disabled={!areaCheckin || !!procesandoId}>
                        {procesandoId ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
                        Confirmar asistencia
                    </button>
                </Modal.Footer>
            </Modal>

            {mostrarEspontaneo && (
                <div className={turneroStyles.overlay} onClick={() => setMostrarEspontaneo(false)}>
                    {/* Mismo ancho que en el turnero: es el mismo formulario y a 480px los
                        campos en dos columnas quedan apretados. */}
                    <div className={`${turneroStyles.contenidoModal} ${turneroStyles.contenidoModalAncho}`} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            modoEspontaneo
                            onCerrar={() => setMostrarEspontaneo(false)}
                            onCreado={confirmarEspontaneo}
                        />
                    </div>
                </div>
            )}

            {turnoDetalle && (
                <div className={turneroStyles.overlay} onClick={cerrarDetalle}>
                    <div className={`${turneroStyles.contenidoModal} ${turneroStyles.contenidoModalDetalle}`} onClick={(e) => e.stopPropagation()}>
                        <ModalDetalleTurno
                            turnoSel={turnoDetalle}
                            onCerrar={cerrarDetalle}
                            onActualizado={handleDetalleActualizado}
                            onEliminado={handleDetalleEliminado}
                            onAsistioRegistrar={handleDetalleAsistio}
                            onVolvioCliente={abrirReingresoDesdeDetalle}
                        />
                    </div>
                </div>
            )}

            <ModalReingresoCliente
                show={!!turnoReingreso}
                turnoOrigen={turnoReingreso}
                tipoInicial={tipoReingresoInicial}
                tipoFijo={tipoReingresoFijo}
                onCerrar={cerrarReingreso}
                onRegistrado={handleReingresoRegistrado}
            />

            <ModalLlegoCliente
                show={flujoLlegada === 'SELECTOR'}
                onCerrar={cerrarFlujoLlegada}
                onElegir={elegirLlegada}
            />

            {(flujoLlegada === 'BUSCAR_EXPRES' || flujoLlegada === 'BUSCAR_REATENCION') && (
                <div className={turneroStyles.overlay} onClick={cerrarFlujoLlegada}>
                    <div className={`${turneroStyles.contenidoModal} ${turneroStyles.contenidoModalAncho}`} onClick={(e) => e.stopPropagation()}>
                        {flujoLlegada === 'BUSCAR_EXPRES' ? (
                            <ModalBuscarAtendidoHoy
                                titulo="Solo entrega / retira documentación"
                                subtitulo="Buscá al cliente entre los atendidos de hoy o en todo el estudio"
                                textoAccion="Registrar exprés"
                                accionExtra={{
                                    texto: 'Cargar datos a mano',
                                    icono: 'bi-person-plus',
                                    onClick: abrirExpresSinCliente,
                                }}
                                onCerrar={cerrarFlujoLlegada}
                                onElegir={elegirAtendidoHoy}
                                onElegirCliente={elegirClientePadron}
                            />
                        ) : (
                            <ModalBuscarAtendidoHoy
                                titulo="Requiere ser atendido nuevamente"
                                subtitulo="Buscá al cliente entre los atendidos de hoy o en todo el estudio"
                                textoAccion="Volver a la cola"
                                onCerrar={cerrarFlujoLlegada}
                                onElegir={elegirAtendidoHoy}
                                onElegirCliente={elegirClientePadron}
                            />
                        )}
                    </div>
                </div>
            )}

            <ModalTramiteExpres
                show={flujoLlegada === 'EXPRES_SIN_TURNO'}
                clienteInicial={clienteExpres}
                onCerrar={cerrarFlujoLlegada}
                onRegistrado={handleExpresRegistrado}
            />

            <CustomModal {...modalState} onHide={hideModal} />
        </div>
    );
};

export default PanelDiario;
